import { useActiveProject } from "@/features/project/ActiveProject";
import { associateDraftOrders, useDraftOrderSearchByPartyId } from "@repo/api";
import { t } from "@repo/translation";
import { ModalSheet, SectionContainer, useToast } from "@repo/ui";
import {
  getDebugMessage,
  trackAnalytics,
  useTypesafeActions,
} from "@repo/utils";
import { useState } from "react";

import {
  type AddDraftOrderState,
  type AddDraftOrderStateActions,
  AddDraftOrderStateDefault,
  addDraftOrderStateActions,
  addDraftOrderStateReducer,
} from "./AddDraftOrderReducer";
import { DraftOrderResultList } from "./DraftOrdersResultList";
import { SearchCustomerDraftOrders } from "./SearchCustomerDraftOrders";

interface AddDraftOrderModalProps {
  isOpen: boolean;
  closeModal: () => void;
  showUnLinkedDraftOrderOverlayItems: boolean;
  associatedDraftOrders: string[];
}

export const AddDraftOrderModal = ({
  isOpen,
  closeModal,
  showUnLinkedDraftOrderOverlayItems,
  associatedDraftOrders,
}: AddDraftOrderModalProps) => {
  const { updateToast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const { opportunity } = useActiveProject();

  const [state, actions] = useTypesafeActions<
    AddDraftOrderState,
    AddDraftOrderStateActions
  >(
    addDraftOrderStateReducer,
    { ...AddDraftOrderStateDefault, defaultPartyId: opportunity?.partyId },
    addDraftOrderStateActions
  );

  const {
    data: customerDraftOrders,
    isValidating: isLoadingCustomerDraftOrders,
  } = useDraftOrderSearchByPartyId(opportunity?.partyId);

  const showDraftOrders = !state.searchValue || state.customerInfo;

  const filteredUnlinkedDraftOrders = !showUnLinkedDraftOrderOverlayItems
    ? customerDraftOrders
    : customerDraftOrders?.filter(
        (item) => !associatedDraftOrders.includes(item.draftOrderId)
      );

  const onClickSave = async () => {
    setIsSaving(true);
    try {
      if (state.draftOrderNumbers && opportunity?.id !== undefined) {
        await associateDraftOrders(state.draftOrderNumbers, opportunity.id);
      }
      updateToast({
        id: state.draftOrderNumbers?.join(","),
        title: t("prompt.draft_orders_saved"),
      });
    } catch (error) {
      updateToast({
        id: state.draftOrderNumbers?.join(","),
        title: t("prompt.draft_order_association_failed"),
        message: getDebugMessage(error),
        type: "error",
      });
    } finally {
      setIsSaving(false);
      closeModal();
    }
  };

  return (
    <ModalSheet
      id="add-draftOrder-modal"
      title={t("action.add_draft_orders")}
      visible={isOpen}
      onClose={closeModal}
      onBack={
        state.customerInfo
          ? () => {
              actions.setCustomer(undefined);
            }
          : undefined
      }
      primaryButtonProps={{
        text:
          (state.draftOrderNumbers?.length ?? 0) > 0
            ? t("action.add_draft_orders_total", {
                total: state.draftOrderNumbers?.length,
              })
            : t("prompt.no_new_draft_order_selected"),
        disabled:
          !state.draftOrderNumbers || state.draftOrderNumbers.length === 0,
        loading: isSaving,
        onClick: () => {
          onClickSave();
          trackAnalytics("add_DraftOrder");
        },
      }}
    >
      <SectionContainer>
        <SearchCustomerDraftOrders state={state} actions={actions} />
        {showDraftOrders && (
          <DraftOrderResultList
            state={state}
            selectDraftOrders={actions.setDraftOrderNumbers}
            isLoading={isLoadingCustomerDraftOrders}
            customerDraftOrders={filteredUnlinkedDraftOrders}
          />
        )}
      </SectionContainer>
    </ModalSheet>
  );
};
